import { Layout } from '@/components/Layout';
import { PageMeta } from '@/components/PageMeta';
import { RelatedArticleSlider } from '@/components/RelatedArticleSlider';
import { SchemaMarkup } from '@/components/SchemaMarkup';
import { VideoBaseContent } from '@/components/VideoBaseContent';
import { Data } from '@/types/content';
import { getCurrentBrand } from '@/utils/getCurrentBrand';
import { getDomain } from '@/utils/getDomain';
import { HOUR } from '@/utils/ttl';
import { withCacheControlHeaders } from '@/utils/withCacheControlHeaders';
import { VIDEO_PAGES_CONFIG } from 'base/configs/video';
import { getVideoFrontPageData } from 'lib/data/video';
import { getVideoRelatedArticles } from 'lib/data/video/getVideoRelatedArticles';
import { getVideoSections } from 'lib/data/video/getVideoSections';
import { mapArticlesDataToSlides } from 'lib/data/video/mapArticlesDataToSlides';
import { TagsList, VideoFrontSection } from 'lib/data/video/types';
import { omitBy } from 'lodash-es';
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import { isUndefined } from 'typesafe-utils';

const brand = getCurrentBrand();

const pageData = {
  meta: {},
  type: 'videoWebPage',
  children: [],
  data: omitBy(
    {
      seoTitle: VIDEO_PAGES_CONFIG.frontPage.seoTitle || '',
      domain: brand.host,
      canonical: `${getDomain()}video`,
      seoDescription: VIDEO_PAGES_CONFIG.frontPage.seoDescription || '',
      keywords: `video, ${brand.name}, videoklipp`,
      ogImageUrl: `${getDomain()}image/${brand.slug}-logo.svg`,
    },
    isUndefined,
  ),
};

interface PageProps {
  sections: VideoFrontSection[];
  tagsList: TagsList['tagsList'];
  relatedArticles: Data[];
}

const VideoPage: NextPage<PageProps> = ({ sections, tagsList, relatedArticles }) => {
  return (
    <Layout
      content={
        <>
          <PageMeta pageData={pageData} />
          <Head>
            <title>{`${brand.name} | Video`}</title>
          </Head>
          <SchemaMarkup pageData={pageData} />
          <VideoBaseContent
            label="Populärt:"
            tagsList={tagsList}
            headline={VIDEO_PAGES_CONFIG.frontPage.pageTitle}
            description={VIDEO_PAGES_CONFIG.frontPage.description ?? ''}
          >
            {getVideoSections(sections)}
            <RelatedArticleSlider link={{ href: '/' }} slider={{ slides: mapArticlesDataToSlides(relatedArticles) }} />
          </VideoBaseContent>
        </>
      }
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const [{ sections, tagsList }, relatedArticles] = await Promise.all([
    getVideoFrontPageData(VIDEO_PAGES_CONFIG.frontPage.sections),
    getVideoRelatedArticles(),
  ]);

  withCacheControlHeaders(context.res, 1 * HOUR);

  return {
    props: { sections, tagsList, relatedArticles },
  };
};

export default VideoPage;
